import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { first } from 'rxjs/operators';

import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { uniqBy } from 'lodash-es';
import * as rxjs from 'rxjs';
import { BackofficeRole } from '../../model-backoffice/auth/cc-backoffice-permission.config';
import { CCBackofficeToken } from '../../model-backoffice/auth/cc-backoffice-token';
import { CCBackofficeUser, CCBackofficeUserConfirmation, CCBackofficeUserInfo, CURRENT_TERMS_VERSION } from '../../model-backoffice/cc-backoffice-user';
import { WINDOW } from '../browser/window.support';
import { PERMISSION_MAP } from './permission-defs';
import { Permission, Role, Session } from './session';
import { environment } from 'src/environments/environment';


@Injectable()
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly oauthService = inject(OAuthService);
  private readonly window = inject<Window>(WINDOW);

  private session?: Session;
  private readonly sessionChangedSubject = new rxjs.ReplaySubject<Session>(1);
  public readonly session$ = this.sessionChangedSubject.asObservable();

  private timer: Observable<number> = rxjs.NEVER;
  private lastTimer = Date.now();

  private sessionUsable = false;
  private initialLoadFinished = false;

  protected currentCCBackofficeToken?: CCBackofficeTokenHolder;

  // run oauth flow at startup
  public loadPromise(organizationId?: string): Promise<Session | undefined> {
    return this.redirectWithCCBackoffice(organizationId);
  }

  async redirectWithCCBackoffice(organizationId: string | undefined): Promise<Session | undefined> {
    // 0. Remember requested org before redirect
    organizationId ??= this.readOrgFromUrl();
    console.log(`Authenticate for organization ${organizationId}`);

    // 1. configure oauth library
    this.oauthService.configure(this.getAuthConfig(organizationId));
    // 2. Setup silent refresh of usertoken, before user token is outdated.
    this.oauthService.setupAutomaticSilentRefresh();


    try {
      await this.oauthService.tryLoginCodeFlow();

      if (this.oauthService.hasValidAccessToken()) {
        try {
          // User is logged in. Get Person data from Control Center
          const u = await this.oauthService.loadUserProfile();
          this.initialLoadFinished = true;
          // Build your session object from person data.
          // You may adjust your session object to your needs (e.g. calculate Permissions)
          this.session = await this.createSession((u as { info: CCBackofficeUserInfo; }).info);
          this.sessionChangedSubject.next(this.session);
          console.info('Session created!');
          this.sessionUsable = true;
          return this.session;
        } catch (err) {
          console.log(err);
          throw err;
        }
      } else {
        // User is not logged in. Library will do redirect with Zaikio

        // Workaround for expired tokens
        if (this.oauthService.getAccessToken()) {
          this.oauthService.logOut();
        }
        this.oauthService.initLoginFlow();

        // just return, because App is finished here.
        return;
      }
    } catch (err) {
      // ACHTUNG: The prompt=none parameter, will lead to an error, if a user is not logged in.
      this.initialLoadFinished = true;

      if (err instanceof OAuthErrorEvent) {
        console.log(err.params, err.reason);
        throw err;
      } else {
        console.log(err);
        throw err;
      }
    }
  }

  public isSessionUsable(): boolean {
    return this.sessionUsable;
  }

  isInitialLoadFinished(): boolean {
    return this.initialLoadFinished;
  }

  private readOrgFromUrl(): string | undefined {
    const queryString = this.window.location.search;
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      const organizationId = urlParams.get('organization_id');
      if (organizationId) {
        return organizationId;
      }
    }

    return undefined;
  }


  initTimer() {
    this.timer = timer(10000, 6000 * 1);
    this.timer.subscribe(_t => {
      const currentTime = Date.now();
      if (currentTime - this.lastTimer > (60000 * 60)) {
        this.window.location.reload();
      }
      this.lastTimer = currentTime;

    });
  }

  public getSessionChangedSubscription(): Observable<Session> {
    return this.session$;
  }


  private transformRoles(backofficeRoles: BackofficeRole[]): { roles: Role[]; permissions: Permission[] } {
    const roles: Role[] = backofficeRoles
      .map(role => role.id)
      .filter((roleId): roleId is Role => PERMISSION_MAP.has(roleId));
    const permissions: Permission[] = uniqBy(roles.flatMap(roleId => PERMISSION_MAP.get(roleId)!), 'id');
    return { roles, permissions };
  }


  private async createSession(info: CCBackofficeUserInfo): Promise<Session> {
    const { roles, permissions } = this.transformRoles(info.roles);
    return {
      name: info.name,
      organizationId: info.organizationId,
      avatarUrl: info.avatarUrl,
      email: info.email,
      id: info.oid,
      locale: info.locale,
      unitSystem: 'metric',
      roles,
      permissions,
      ssus: info.ssus,
      adGroups: info.adGroups,
      hdmCustomerNumber: info.hdmCustomerNumber
    };
  }

  logout() {
    this.oauthService.logOut();
    this.window.location.assign(`${environment.ccBackofficeBaseUrl}/#/oauth/logout`);
  }


  switchOrganization(organizationId: string) {
    this.oauthService.logOut();

    void this.loadPromise(organizationId === '' ? undefined : organizationId);
  }

  public getSession(): Session | undefined {
    return this.session;
  }

  public getLocale(): string {
    return this.session?.locale || 'en';
  }

  public async checkConfirmations(): Promise<boolean> {
    const confirmation = await rxjs.firstValueFrom(this.http.get<CCBackofficeUserConfirmation>(`${this.getUserUrl()}/current-user/confirmations`));
    return confirmation.termsVersion === CURRENT_TERMS_VERSION;
  }

  public createConfirmation(): Observable<CCBackofficeUserConfirmation> {
    return this.http.post<CCBackofficeUserConfirmation>(`${this.getUserUrl()}/current-user/confirmations`, {}).pipe(
      first(),
    );
  }

  checkRoute(url: string): boolean {
    if (this.session === undefined) {
      return false;
    }
    return this.session.permissions.some(permission => {
      return permission.routes?.some(route =>
        url.includes(route.appPath) && (!route.pagePath || url.includes(route.pagePath))
      );
    });
  }

  getCurrentUser(): Promise<CCBackofficeUser> {
    return rxjs.firstValueFrom(this.http.get<CCBackofficeUser>(`${this.getUserUrl()}/current-user`));
  }

  clearHxToken() {
    this.currentCCBackofficeToken = undefined;
  }

  private getUserUrl(): string {
    return `${environment.ccBackofficeBaseUrl}/api/backoffice/users`;
  }

  private getAuthConfig(organizationId: string | undefined): AuthConfig {
    const directoryUrl = environment.ccBackofficeBaseUrl;
    const apiUrl = `${directoryUrl}/api/backoffice/public/v1`;

    const queryParams: Record<string, unknown> = {};
    if (organizationId) {
      queryParams.organizationId = organizationId;
    }

    return {
      // Use PKCE
      oidc: false,
      // Use Oauth Code Flow
      responseType: 'code',
      issuer: directoryUrl,

      // Must be URLs of the heidelberg platform in Zaikio
      loginUrl: `${directoryUrl}/#/oauth`,
      //logoutUrl: `${directoryUrl}/session.logout`,
      tokenEndpoint: `${apiUrl}/oauth/access_token`,
      // this is the person endpoint of the control center
      userinfoEndpoint: `${apiUrl}/person`,

      customQueryParams: queryParams,

      // jumb back to current URL
      redirectUri: window.location.origin,

      // TODO IMPORTANT set your client id here
      clientId: 'backoffice_sample_app',
      // For now we don't have scopes.
      scope: 'openid email',
      // switch this off in production
      showDebugInformation: true,
      // workaround for bug https://github.com/manfredsteyer/angular-oauth2-oidc/issues/1135
      clockSkewInSec: 60,
    };
  }
}

export interface CCBackofficeTokenHolder {
  token: CCBackofficeToken;
  expiringAt: Date;
}
